import React, { useEffect, useRef, useState } from 'react';
import type { AnimationItem } from 'lottie-web';
import { cn } from '../ui';
import { BhajanVideoModal } from './BhajanVideoModal';
import { youtubeVideoId } from './youtubeVideoId';
import { babiesAnimationFile, babiesAnimationPlayCount } from './deityIdentity';
import { deityIconUrl } from './deityIcon';

export interface BhajanBabyProps {
  /**
   * Deity node id — the language-independent key for the animation. The
   * displayed deity name is Telugu/Kannada/… script outside English, so it can
   * never choose the asset; see deityIdentity.
   */
  deityId?: number | null;
  /** ASCII deity name, used only when `deityId` is one this build predates. */
  deityNameEn?: string | null;
  /** Deity name as the reader sees it — for the accessible label only. */
  deityName?: string | null;
  /**
   * Layout mode:
   *   'card'  — fills the parent container width at a 16:9 aspect, like
   *             native's BhajanBabiesPromoView. Use inside the bhajan-detail
   *             left column.
   *   number  — fixed pixel size (square). For thumbnails / inline use.
   */
  size?: number | 'card';
  /** YouTube link to open when the user clicks the animation. */
  youtubeUrl?: string | null;
  /** Bhajan name, used to label the video dialog for screen readers. */
  title?: string | null;
  className?: string;
}

/**
 * The Bhajan Babies promo: a deity Lottie animation (lazy-loaded) that opens
 * the bhajan's video, mirroring jnana-native's BhajanBabiesPromoView.
 *
 * Only seven deities have an animation. The rest — ugadi, tattva, "other",
 * stotram — still have videos, so the card falls back to the static deity
 * image rather than vanishing, exactly as native does. Uses lottie-web
 * (smaller bundle than lottie-react).
 */
export const BhajanBaby: React.FC<BhajanBabyProps> = ({
  deityId,
  deityNameEn,
  deityName,
  size = 96,
  youtubeUrl,
  title,
  className,
}) => {
  const file = babiesAnimationFile(deityId, deityNameEn);
  const numberOfPlays = babiesAnimationPlayCount(deityId, deityNameEn);
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const [ready, setReady] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const videoId = youtubeVideoId(youtubeUrl);

  useEffect(() => {
    if (!file || !containerRef.current) return;
    const el = containerRef.current;
    let cancelled = false;
    // `loop: false` means the `complete` event represents one whole play. A
    // numeric lottie-web loop is a number of *repeats*, not total plays, so we
    // count complete plays ourselves to match native exactly.
    let playsCompleted = 0;

    const start = () => {
      if (cancelled) return;
      // dynamic import keeps lottie-web out of initial bundle
      import('lottie-web').then(({ default: lottie }) => {
        if (cancelled || !el) return;
        animRef.current = lottie.loadAnimation({
          container: el,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          path: `/lottie/babies/${file}`,
        });
        animRef.current.setSpeed(0.6);
        animRef.current.addEventListener('DOMLoaded', () => setReady(true));
        animRef.current.addEventListener('complete', () => {
          playsCompleted += 1;
          if (numberOfPlays != null && playsCompleted < numberOfPlays) {
            animRef.current?.goToAndPlay(0, true);
          }
        });
      });
    };

    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(start, { timeout: 800 })
      : window.setTimeout(start, 200);

    return () => {
      cancelled = true;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      try { animRef.current?.destroy?.(); } catch { /* ignore */ }
      animRef.current = null;
    };
  }, [file, numberOfPlays]);

  // Nothing to draw and nothing to open — render nothing at all. A deity with
  // no animation but WITH a video still renders: it falls back to the still
  // image below, so the reader keeps the link to the video.
  if (!file && !youtubeUrl) return null;

  const isCard = size === 'card';
  // 16:9 like native's BhajanBabiesPromoView (1920x1080 source aspect).
  const wrapStyle: React.CSSProperties = isCard
    ? { width: '100%', aspectRatio: '16 / 9' }
    : { width: size as number, height: size as number };

  const lottieStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    // Screen blending is what makes the animations' dark matte disappear into
    // the card. The still fallback is a transparent PNG and must NOT get it —
    // screen would wash the artwork out against the light themes.
    mixBlendMode: 'screen',
  };

  const inner = (
    <div
      className={cn(
        'relative transition-opacity duration-400',
        // The still has no DOMLoaded event to wait for, so it must not be
        // held at opacity-0 forever.
        !file || ready ? 'opacity-100' : 'opacity-0',
        isCard && 'overflow-hidden rounded-2xl border border-light bg-bg-2/60',
        className
      )}
      style={wrapStyle}
    >
      {file ? (
        <div ref={containerRef} aria-hidden style={lottieStyle} />
      ) : (
        <span className="flex h-full w-full items-center justify-center">
          <img
            src={deityIconUrl(deityId)}
            alt=""
            aria-hidden
            loading="lazy"
            className="max-h-[70%] max-w-[40%] object-contain"
          />
        </span>
      )}
    </div>
  );

  // Visual overlay shared by the play button + label (purely decorative).
  const overlay = (
    <>
      <span
        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none"
        aria-hidden
      >
        <span className="rounded-full bg-bg-0/70 backdrop-blur-sm border border-light p-2">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <path d="M8 5v14l11-7z" fill="white" />
          </svg>
        </span>
      </span>
      {isCard && (
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-md bg-bg-0/70 backdrop-blur-sm border border-light px-2.5 py-1.5 text-[13px] text-text-secondary pointer-events-none">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
            <path fill="#FF0000" d="M23 7.6a3 3 0 0 0-2.1-2.1C19.1 5 12 5 12 5s-7.1 0-8.9.5A3 3 0 0 0 1 7.6 31 31 0 0 0 .5 12 31 31 0 0 0 1 16.4a3 3 0 0 0 2.1 2.1c1.8.5 8.9.5 8.9.5s7.1 0 8.9-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 23.5 12 31 31 0 0 0 23 7.6z" />
            <path fill="white" d="M9.75 15.5V8.5l6 3.5z" />
          </svg>
          Watch video
        </span>
      )}
    </>
  );

  // Preferred path: play in an in-site modal so the view counts for the channel.
  if (youtubeUrl && videoId) {
    return (
      <>
        <button
          type="button"
          onClick={() => setVideoOpen(true)}
          aria-label={`Play the Bhajan Babies video${deityName ? ` for ${deityName}` : ''}`}
          className={cn('group relative block text-left', isCard ? 'w-full' : 'inline-block')}
        >
          {inner}
          {overlay}
        </button>
        <BhajanVideoModal
          videoId={videoId}
          isOpen={videoOpen}
          onClose={() => setVideoOpen(false)}
          title={title ?? deityName ?? undefined}
        />
      </>
    );
  }

  // Fallback: URL we couldn't parse a video id from — open YouTube directly.
  if (youtubeUrl) {
    return (
      <a
        href={youtubeUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="Watch the Bhajan Babies video on YouTube"
        aria-label="Watch the Bhajan Babies video on YouTube"
        className={cn('group relative block', isCard ? 'w-full' : 'inline-block')}
      >
        {inner}
        {overlay}
      </a>
    );
  }

  return inner;
};
